import React, { useState, useEffect } from 'react';
import { api, getStation } from '../api';

function Section({ title, children }) {
  return (
    <div style={{ background:'#fff', borderRadius:12, padding:22, border:'1px solid #f1f5f9', marginBottom:16 }}>
      <h3 style={{ margin:'0 0 18px', fontSize:14, fontWeight:700, color:'#0f172a', borderBottom:'1px solid #f1f5f9', paddingBottom:12 }}>{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type='text', hint }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>{label}</label>
      {type === 'textarea' ? (
        <textarea value={value} onChange={onChange} rows={3} style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', resize:'vertical', boxSizing:'border-box' }} onFocus={e=>e.target.style.borderColor='#6366f1'} onBlur={e=>e.target.style.borderColor='#e2e8f0'} />
      ) : (
        <input type={type} value={value} onChange={onChange} style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} onFocus={e=>e.target.style.borderColor='#6366f1'} onBlur={e=>e.target.style.borderColor='#e2e8f0'} />
      )}
      {hint && <p style={{ margin:'4px 0 0', fontSize:11, color:'#94a3b8' }}>{hint}</p>}
    </div>
  );
}

export default function Settings() {
  const station = getStation();
  const [profile, setProfile] = useState({ name:'', email:'', timezone:'America/New_York' });
  const [stConfig, setStConfig] = useState({ sftp_host:'', sftp_user:'', sftp_path:'/audio/', rss_secret:'', ad_network_enabled:false, ad_floor_cpm:8, station_ident_text:'' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const set = (k,v) => setProfile(p => ({...p,[k]:v}));
  const setSt = (k,v) => setStConfig(p => ({...p,[k]:v}));

  useEffect(() => {
    api.get('/broadcast/settings').catch(() => null).then(d => { if (d) setProfile(p => ({...p,...d})); });
    if (station) api.get(`/broadcast/stations/${station.id}/settings`).catch(() => null).then(d => { if (d) setStConfig(c => ({...c,...d})); });
  }, [station]);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.put('/broadcast/settings', profile);
      if (station) await api.put(`/broadcast/stations/${station.id}/settings`, stConfig);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth:680 }}>
      <Section title="👤 Account Profile">
        <Field label="Full Name"     value={profile.name}     onChange={e=>set('name',e.target.value)}     />
        <Field label="Email Address" value={profile.email}    onChange={e=>set('email',e.target.value)}    type="email" />
        <Field label="Timezone"      value={profile.timezone} onChange={e=>set('timezone',e.target.value)} hint="Used for scheduling AI host sessions and show delivery" />
      </Section>

      {station && (
        <>
          <Section title="📡 Station Delivery Settings">
            <p style={{ margin:'0 0 16px', fontSize:13, color:'#64748b' }}>Configure how Show Factory delivers content to your playout system.</p>
            <Field label="SFTP Host"     value={stConfig.sftp_host} onChange={e=>setSt('sftp_host',e.target.value)} hint="e.g. files.yourstation.com" />
            <Field label="SFTP Username" value={stConfig.sftp_user} onChange={e=>setSt('sftp_user',e.target.value)} />
            <Field label="SFTP Path"     value={stConfig.sftp_path} onChange={e=>setSt('sftp_path',e.target.value)} hint="Directory where shows should be delivered" />
            <Field label="RSS Feed Secret" value={stConfig.rss_secret} onChange={e=>setSt('rss_secret',e.target.value)} hint="Your private RSS feed URL includes this token" />
          </Section>

          <Section title="💰 Ad Network Settings">
            <p style={{ margin:'0 0 14px', fontSize:13, color:'#64748b' }}>Control how your station participates in the Groovie programmatic ad network. You earn 65% of all ad revenue served on your station.</p>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 0', borderBottom:'1px solid #f8fafc', marginBottom:14 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:'#0f172a' }}>Enable Ad Network</div>
                <div style={{ fontSize:12, color:'#64748b' }}>Allow programmatic ads to run during your scheduled ad breaks</div>
              </div>
              <div onClick={() => setSt('ad_network_enabled', !stConfig.ad_network_enabled)} style={{
                width:44, height:24, borderRadius:12, background: stConfig.ad_network_enabled ? '#6366f1' : '#e2e8f0',
                position:'relative', cursor:'pointer', transition:'background 0.2s', flexShrink:0
              }}>
                <div style={{ position:'absolute', top:2, left: stConfig.ad_network_enabled ? 22 : 2, width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
              </div>
            </div>
            <Field label="Floor CPM ($)" value={stConfig.ad_floor_cpm} onChange={e=>setSt('ad_floor_cpm',+e.target.value)} type="number" hint="Minimum CPM you'll accept. We recommend $8–$12." />
          </Section>

          <Section title="🎙️ Station Identity">
            <Field label="Station Ident Text" value={stConfig.station_ident_text} onChange={e=>setSt('station_ident_text',e.target.value)} type="textarea" hint="Text injected into AI host station ID scripts. E.g. 'You're listening to WKRS 97.5 — Memphis's Home for R&B'" />
          </Section>
        </>
      )}

      <div style={{ display:'flex', justifyContent:'flex-end', gap:10, alignItems:'center' }}>
        {saved && <span style={{ color:'#15803d', fontSize:13, fontWeight:600 }}>✓ Settings saved</span>}
        <button onClick={save} disabled={saving} style={{ padding:'10px 28px', borderRadius:8, border:'none', background: saving ? '#c7d2fe' : 'linear-gradient(135deg,#6366f1,#4f46e5)', color:'#fff', fontWeight:700, fontSize:13, cursor: saving?'not-allowed':'pointer' }}>
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}
